import { BufferAttribute, BufferGeometry, ClampToEdgeWrapping, DataTexture, LinearFilter, Mesh, NoColorSpace, RGBAFormat, Sphere, UnsignedByteType, Vector3 } from 'three';
import type { Object3D, ShaderMaterial } from 'three';
import type { QualityPreset } from '@cloudpearl/config';
import { TILE, tileKey } from '@cloudpearl/assets/worldgen';
import { createTerrainDepthMaterial } from '@cloudpearl/assets';
import type { Lod, TileData, UndersideData } from '@cloudpearl/assets/worldgen';
import type { TileStreamer } from './TileStreamer';
import type { LocalHeightfield } from './LocalHeightfield';

/** Render layer for far-only geometry: the main camera sees it, the reflection probe does not. */
export const FAR_LAYER = 1;

interface TileView {
  ti: number;
  tj: number;
  lod: Lod | -1;
  empty: boolean;
  mesh: Mesh | null;
  tex: DataTexture | null;
}

/**
 * Streams 64 m terrain tiles around the camera with distance LOD (1/2/4/8 m vertices), swapping a
 * tile only once its replacement has arrived so nothing pops out. Sky Isles also stream the rocky
 * undersides of islands, built once per island from its true coastline.
 */
export class Terrain {
  private readonly tiles = new Map<number, TileView>();
  private readonly undersides = new Map<number, { ti: number; tj: number; meshes: Mesh[] }>();
  private readonly lod0Radius: number;
  private readonly depthMaterial = createTerrainDepthMaterial();

  constructor(
    private readonly parent: Object3D,
    private readonly material: ShaderMaterial,
    private readonly cliffMaterial: ShaderMaterial,
    private readonly streamer: TileStreamer,
    private readonly heightfield: LocalHeightfield,
    private readonly quality: QualityPreset,
    private readonly islands: boolean,
    grassOuter: number,
  ) {
    this.lod0Radius = Math.max(quality.terrainLod[0], grassOuter + 60);
  }

  private wantLod(d: number, current: Lod | -1): Lod {
    const r = [this.lod0Radius, this.quality.terrainLod[1], this.quality.terrainLod[2]];
    // Hysteresis: keep the current LOD within ±10 m of a threshold.
    for (let l = 0; l < 3; l++) {
      const edge = r[l]! + (current !== -1 && current <= l ? 10 : -10);
      if (d < edge) return l as Lod;
    }
    return 3;
  }

  /**
   * Request/unload around the player (x, z). LOD is measured from the player (grass and the
   * heightfield need full detail all around it); (ax, az) — a point ahead — only sets priority.
   */
  update(x: number, z: number, ax = x, az = z): void {
    const R = this.quality.viewDistance;
    const ti0 = Math.floor((x - R) / TILE);
    const ti1 = Math.floor((x + R) / TILE);
    const tj0 = Math.floor((z - R) / TILE);
    const tj1 = Math.floor((z + R) / TILE);
    for (let tj = tj0; tj <= tj1; tj++) {
      for (let ti = ti0; ti <= ti1; ti++) {
        const cx = (ti + 0.5) * TILE;
        const cz = (tj + 0.5) * TILE;
        const d = Math.hypot(cx - x, cz - z);
        if (d > R + 46) continue;
        const key = tileKey(ti, tj);
        let v = this.tiles.get(key);
        if (!v) {
          v = { ti, tj, lod: -1, empty: false, mesh: null, tex: null };
          this.tiles.set(key, v);
        }
        const pd = Math.min(d, Math.hypot(cx - ax, cz - az));
        // Undersides are requested for open-sky tiles too: the queue is rebuilt every pass, and
        // `ready()` waits on every tile's (usually empty) list — skipping them left Sky Isles
        // loads sitting out the full 8 s timeout.
        if (this.islands && !this.undersides.has(key) && d < R * 1.1) this.streamer.requestIslands(ti, tj, pd * 1.2 + 40);
        if (v.empty) continue;
        const want = this.wantLod(d, v.lod);
        if (want !== v.lod) this.streamer.requestTile(ti, tj, want, pd * (v.lod === -1 ? 1 : 1.6) + want * 30);
      }
    }
    // Unload far tiles.
    for (const [key, v] of this.tiles) {
      const d = Math.hypot((v.ti + 0.5) * TILE - x, (v.tj + 0.5) * TILE - z);
      if (d > R + 110) {
        this.dropTile(v);
        this.tiles.delete(key);
      }
    }
    for (const [key, u] of this.undersides) {
      const d = Math.hypot((u.ti + 0.5) * TILE - x, (u.tj + 0.5) * TILE - z);
      if (d > R * 1.1 + 160) {
        for (const m of u.meshes) {
          this.parent.remove(m);
          m.geometry.dispose();
        }
        this.undersides.delete(key);
      }
    }
  }

  /** True once every tile within `radius` of (x, z) has arrived (empty or not). */
  ready(x: number, z: number, radius: number): boolean {
    for (const v of this.tiles.values()) {
      const d = Math.hypot((v.ti + 0.5) * TILE - x, (v.tj + 0.5) * TILE - z);
      if (d < radius && !v.empty && v.lod === -1) return false;
    }
    if (this.islands) {
      for (const [key, v] of this.tiles) {
        const d = Math.hypot((v.ti + 0.5) * TILE - x, (v.tj + 0.5) * TILE - z);
        if (d < radius && !this.undersides.has(key)) return false;
      }
    }
    return this.tiles.size > 0;
  }

  addTile(t: TileData): void {
    const key = tileKey(t.ti, t.tj);
    const v = this.tiles.get(key);
    if (!v) return;
    if (t.empty) {
      this.dropTile(v);
      v.empty = true;
      v.lod = t.lod;
      return;
    }
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(t.position, 3));
    g.setAttribute('aTile', new BufferAttribute(t.tile, 2));
    g.setIndex(new BufferAttribute(t.index, 1));
    const midY = (t.minY + t.maxY) / 2;
    const hy = (t.maxY - t.minY) / 2;
    g.boundingSphere = new Sphere(new Vector3(TILE / 2, midY, TILE / 2), Math.sqrt(TILE * TILE * 0.5 + hy * hy) + 1);
    const tex = new DataTexture(t.normalTex, t.n, t.n, RGBAFormat, UnsignedByteType);
    tex.minFilter = LinearFilter;
    tex.magFilter = LinearFilter;
    tex.wrapS = tex.wrapT = ClampToEdgeWrapping;
    tex.generateMipmaps = false;
    tex.colorSpace = NoColorSpace;
    tex.needsUpdate = true;
    const mesh = new Mesh(g, this.material);
    mesh.position.set(t.ti * TILE, 0, t.tj * TILE);
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();
    mesh.receiveShadow = true;
    mesh.castShadow = t.lod <= 1;
    // Far tiles are a hazy sliver at the horizon of the pearl's reflection: the probe skips them.
    if (t.lod >= 2) mesh.layers.set(FAR_LAYER);
    const mat = this.material;
    mesh.onBeforeRender = () => {
      mat.uniforms['tNormal']!.value = tex;
      mat.uniformsNeedUpdate = true;
    };
    const depth = this.depthMaterial;
    mesh.customDepthMaterial = depth;
    mesh.onBeforeShadow = () => {
      depth.uniforms['tNormal']!.value = tex;
      depth.uniformsNeedUpdate = true;
    };
    this.dropTile(v);
    v.mesh = mesh;
    v.tex = tex;
    v.lod = t.lod;
    this.parent.add(mesh);
    if (t.lod === 0) this.heightfield.addTile(t.ti, t.tj, t.hm);
  }

  addUndersides(ti: number, tj: number, list: UndersideData[]): void {
    const key = tileKey(ti, tj);
    if (this.undersides.has(key)) return;
    const meshes: Mesh[] = [];
    for (const u of list) {
      const g = new BufferGeometry();
      g.setAttribute('position', new BufferAttribute(u.position, 3));
      g.setAttribute('normal', new BufferAttribute(u.normal, 3));
      g.setAttribute('color', new BufferAttribute(u.color, 3));
      g.setAttribute('uv', new BufferAttribute(u.uv, 2));
      g.setAttribute('aDepth', new BufferAttribute(u.depth, 1));
      g.setIndex(new BufferAttribute(u.index, 1));
      g.computeBoundingSphere();
      const m = new Mesh(g, this.cliffMaterial);
      m.matrixAutoUpdate = false;
      m.receiveShadow = true;
      // No casting: the island's top tiles cast its shadow; a rim-only shadow from a far island
      // (whose top is a non-casting LOD) would draw a stray sliver across the meadow.
      m.castShadow = false;
      this.parent.add(m);
      meshes.push(m);
    }
    this.undersides.set(key, { ti, tj, meshes });
  }

  private dropTile(v: TileView): void {
    if (v.mesh) {
      this.parent.remove(v.mesh);
      v.mesh.geometry.dispose();
      v.mesh = null;
    }
    if (v.tex) {
      v.tex.dispose();
      v.tex = null;
    }
    if (v.lod === 0) this.heightfield.removeTile(v.ti, v.tj);
  }

  clear(): void {
    for (const v of this.tiles.values()) this.dropTile(v);
    this.tiles.clear();
    for (const u of this.undersides.values()) {
      for (const m of u.meshes) {
        this.parent.remove(m);
        m.geometry.dispose();
      }
    }
    this.undersides.clear();
  }

  dispose(): void {
    this.clear();
    this.depthMaterial.dispose();
  }

  get stats(): { tiles: number; undersides: number } {
    let n = 0;
    for (const v of this.tiles.values()) if (v.mesh) n++;
    return { tiles: n, undersides: this.undersides.size };
  }
}
